import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { apiRequest, getUserDetails } from "../../services/apiRequest";
import { requestType } from "../../services/requests";

// --- Async Thunks ---

// Both Royal Momentum and Royal Bridge pass `useV2: true` to hit the V2 endpoints. Royal Momentum also passes
// `travelAgencyUid` → agency_id; Royal Bridge passes none, so agencyKey() returns {} (HQ-aggregate,
// no agency key). The V1 branch (travel_agency_uid) is retained for any legacy caller.
// See V2_API_MIGRATION.md.
const agencyKey = (travelAgencyUid, useV2) =>
    travelAgencyUid
        ? useV2
            ? { agency_id: travelAgencyUid }
            : { travel_agency_uid: travelAgencyUid }
        : {};

export const getTotalBooking = createAsyncThunk(
    "commercialPerformance/getTotalBooking",
    async ({ region = "", type, startDate, endDate = null, travelAgencyUid, useV2 = false }) => {
        const userDetails = getUserDetails();
        const response = await apiRequest({
            payload: {
                data: {
                    user_uid: userDetails.user_details.uid,
                    ...agencyKey(travelAgencyUid, useV2),
                    region,
                    type,
                    start_date: startDate,
                    end_date: endDate,
                },
            },
            requestType: useV2 ? requestType.getTotalBookingV2 : requestType.getTotalBooking,
        });
        return response.data;
    }
);

// Revenue/passengers now use the dedicated GET_TOTAL_REVENUE_V2 / GET_TOTAL_PASSENGERS_V2 endpoints,
// which accept the same type/start_date/end_date shape as every other card here.
//
// They previously borrowed getTotalRevenueWithTargetV2 / getTotalPassengersWithTargetV2 because no
// plain V2 existed yet. Those only accept year + quarter, so the month/week/date-range filters had
// to be collapsed to a year and both cards reported full-year figures while the rest of the
// dashboard reported the selected period — the caveat recorded in RoyalMomentumSalesDashboard.md.
// Switching endpoint removes that discrepancy; no component change was needed because both
// consumers (RoyalMomentumSalesDashboard, RoyalBridgeCommercialPerformance) only read
// `total_revenue` / `total_passengers`, which both endpoints return.
//
// The WITH_TARGET pair is still the right choice where `target`/`target_achieved` are displayed —
// that is the Plans & Targets page, which calls them through royalBridgePlansAndTargetsApi.js.
export const getTotalRevenue = createAsyncThunk(
    "commercialPerformance/getTotalRevenue",
    async ({ region = null, type, startDate, endDate = null, travelAgencyUid, useV2 = false }) => {
        const userDetails = getUserDetails();
        const response = await apiRequest({
            payload: {
                data: {
                    user_uid: userDetails.user_details.uid,
                    ...agencyKey(travelAgencyUid, useV2),
                    region,
                    type,
                    start_date: startDate,
                    end_date: endDate,
                },
            },
            requestType: useV2 ? requestType.getTotalRevenueV2 : requestType.getTotalRevenue,
        });
        return response.data;
    }
);

export const getTotalPassengers = createAsyncThunk(
    "commercialPerformance/getTotalPassengers",
    async ({ region = null, type, startDate, endDate = null, travelAgencyUid, useV2 = false }) => {
        const userDetails = getUserDetails();
        const response = await apiRequest({
            payload: {
                data: {
                    user_uid: userDetails.user_details.uid,
                    ...agencyKey(travelAgencyUid, useV2),
                    region,
                    type,
                    start_date: startDate,
                    end_date: endDate,
                },
            },
            requestType: useV2 ? requestType.getTotalPassengersV2 : requestType.getTotalPassengers,
        });
        return response.data;
    }
);

export const getTotalSailing = createAsyncThunk(
    "commercialPerformance/getTotalSailing",
    async ({ region = "", year, travelAgencyUid, useV2 = false }) => {
        const userDetails = getUserDetails();
        const response = await apiRequest({
            payload: {
                data: {
                    user_uid: userDetails.user_details.uid,
                    ...agencyKey(travelAgencyUid, useV2),
                    region,
                    year,
                },
            },
            requestType: useV2 ? requestType.getTotalSailingV2 : requestType.getTotalSailing,
        });
        return response.data;
    }
);

export const getProductMix = createAsyncThunk(
    "commercialPerformance/getProductMix",
    async ({ type, startDate, endDate = null, travelAgencyUid, useV2 = false }) => {
        const userDetails = getUserDetails();
        const response = await apiRequest({
            payload: {
                data: {
                    user_uid: userDetails.user_details.uid,
                    ...agencyKey(travelAgencyUid, useV2),
                    type,
                    start_date: startDate,
                    end_date: endDate,
                },
            },
            requestType: useV2 ? requestType.getProductMixV2 : requestType.getProductMix,
        });
        return response.data;
    }
);

export const getStateroomMix = createAsyncThunk(
    "commercialPerformance/getStateroomMix",
    async ({ region = null, type, startDate, endDate = null, travelAgencyUid, useV2 = false }) => {
        const userDetails = getUserDetails();
        const response = await apiRequest({
            payload: {
                data: {
                    user_uid: userDetails.user_details.uid,
                    ...agencyKey(travelAgencyUid, useV2),
                    region,
                    type,
                    start_date: startDate,
                    end_date: endDate,
                },
            },
            requestType: useV2 ? requestType.getStateroomMixV2 : requestType.getStateroomMix,
        });
        return response.data;
    }
);

export const getTop10Cruises = createAsyncThunk(
    "commercialPerformance/getTop10Cruises",
    async ({ region = "", type, startDate, endDate = null, travelAgencyUid, useV2 = false, limit = 10 }) => {
        const userDetails = getUserDetails();
        const response = await apiRequest({
            payload: {
                data: {
                    user_uid: userDetails.user_details.uid,
                    ...agencyKey(travelAgencyUid, useV2),
                    region,
                    type,
                    start_date: startDate,
                    end_date: endDate,
                    ...(useV2 && { limit }),
                },
            },
            requestType: useV2 ? requestType.getTop10CruisesV2 : requestType.getTop10Cruises,
        });
        return response.data;
    }
);

export const getBookingToDeparture = createAsyncThunk(
    "commercialPerformance/getBookingToDeparture",
    async ({ region = "", year, travelAgencyUid, useV2 = false }) => {
        const userDetails = getUserDetails();
        const response = await apiRequest({
            payload: {
                data: {
                    user_uid: userDetails.user_details.uid,
                    ...agencyKey(travelAgencyUid, useV2),
                    region,
                    year,
                },
            },
            requestType: useV2
                ? requestType.getBookingToDepartureV2
                : requestType.getBookingToDeparture,
        });
        return response.data;
    }
);

export const getStorePerformance = createAsyncThunk(
    "commercialPerformance/getStorePerformance",
    async ({ region = "", type, startDate, endDate = null, travelAgencyUid, useV2 = false, limit = 10 }) => {
        const userDetails = getUserDetails();
        const response = await apiRequest({
            payload: {
                data: {
                    user_uid: userDetails.user_details.uid,
                    ...agencyKey(travelAgencyUid, useV2),
                    region,
                    type,
                    start_date: startDate,
                    end_date: endDate,
                    ...(useV2 && { limit }),
                },
            },
            requestType: useV2 ? requestType.getStorePerformanceV2 : requestType.getStorePerformance,
        });
        return response.data;
    }
);

export const getShipMix = createAsyncThunk(
    "commercialPerformance/getShipMix",
    async ({ region = "", type, startDate, endDate = null, travelAgencyUid, useV2 = false }) => {
        const userDetails = getUserDetails();
        const response = await apiRequest({
            payload: {
                data: {
                    user_uid: userDetails.user_details.uid,
                    ...agencyKey(travelAgencyUid, useV2),
                    region,
                    type,
                    start_date: startDate,
                    end_date: endDate,
                },
            },
            requestType: useV2 ? requestType.getShipMixV2 : requestType.getShipMix,
        });
        return response.data;
    }
);

export const getRegions = createAsyncThunk(
    "commercialPerformance/getRegions",
    async () => {
        const response = await apiRequest({
            payload: {
                data: {
                    user_uid: getUserDetails().user_details.uid,
                },
            },
            requestType: requestType.getRegions,
        });
        return response.data;
    }
);

export const getCountries = createAsyncThunk(
    "commercialPerformance/getCountries",
    async () => {
        const response = await apiRequest({
            payload: {
                data: {
                    user_uid: getUserDetails().user_details.uid,
                },
            },
            requestType: requestType.getCountries,
        });
        return response.data;
    }
);

export const getWeekRange = createAsyncThunk(
    "commercialPerformance/getWeekRange",
    async ({ type, date }) => {
        const response = await apiRequest({
            payload: {
                data: {
                    user_uid: getUserDetails().user_details.uid,
                    type,
                    date,
                },
            },
            requestType: requestType.getWeekRange,
        });
        return response.data;
    }
);

// --- Helper to build extraReducers for each thunk ---

const thunks = {
    totalBooking: getTotalBooking,
    totalRevenue: getTotalRevenue,
    totalPassengers: getTotalPassengers,
    totalSailing: getTotalSailing,
    productMix: getProductMix,
    stateroomMix: getStateroomMix,
    top10Cruises: getTop10Cruises,
    bookingToDeparture: getBookingToDeparture,
    storePerformance: getStorePerformance,
    shipMix: getShipMix,
    regions: getRegions,
    countries: getCountries,
    weekRange: getWeekRange,
};

const buildInitialDataState = () => {
    const state = {};
    Object.keys(thunks).forEach((key) => {
        state[key] = { data: null, loading: false, error: null };
    });
    return state;
};

const initialState = buildInitialDataState();

// --- Slice ---

export const commercialPerformanceSlice = createSlice({
    name: "commercialPerformance",
    initialState,
    reducers: {
        clearCommercialPerformance: () => buildInitialDataState(),
    },
    extraReducers: (builder) => {
        Object.entries(thunks).forEach(([key, thunk]) => {
            builder
                .addCase(thunk.pending, (state) => {
                    state[key].loading = true;
                    state[key].error = null;
                })
                .addCase(thunk.fulfilled, (state, action) => {
                    state[key].loading = false;
                    state[key].data = action.payload;
                })
                .addCase(thunk.rejected, (state, action) => {
                    state[key].loading = false;
                    state[key].error = action.error.message;
                });
        });
    },
});

export const { clearCommercialPerformance } = commercialPerformanceSlice.actions;

// --- Selectors ---

export const selectTotalBooking = (state) => state.commercialPerformance.totalBooking;
export const selectTotalRevenue = (state) => state.commercialPerformance.totalRevenue;
export const selectTotalPassengers = (state) => state.commercialPerformance.totalPassengers;
export const selectTotalSailing = (state) => state.commercialPerformance.totalSailing;
export const selectProductMix = (state) => state.commercialPerformance.productMix;
export const selectStateroomMix = (state) => state.commercialPerformance.stateroomMix;
export const selectTop10Cruises = (state) => state.commercialPerformance.top10Cruises;
export const selectBookingToDeparture = (state) => state.commercialPerformance.bookingToDeparture;
export const selectStorePerformance = (state) => state.commercialPerformance.storePerformance;
export const selectShipMix = (state) => state.commercialPerformance.shipMix;
export const selectRegions = (state) => state.commercialPerformance.regions;
export const selectCountries = (state) => state.commercialPerformance.countries;
export const selectWeekRange = (state) => state.commercialPerformance.weekRange;

export default commercialPerformanceSlice.reducer;
